"use client";

import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { InviteCard } from "./app-ui/invite-card";

/**
 * A battle invite with both required fields present (invitationId and battleId
 * are optional in the wire type but required to respond via the API).
 */
export interface BattleInvite {
    invitationId: string;
    battleId: string;
    fromUserId: number;
    fromDisplayUsername?: string;
    toUserId: number;
    duration?: number;
}

interface BattleInviteItemProps {
    invite: BattleInvite;
    onRemove: (battleId: string) => void;
}

export function BattleInviteItem({ invite, onRemove }: BattleInviteItemProps) {
    const t = useTranslations("puzzles");
    const router = useRouter();
    const [isResponding, setIsResponding] = useState(false);

    const respond = async (status: "accepted" | "declined") => {
        if (isResponding) return;
        setIsResponding(true);

        let accepted = false;
        try {
            const res = await fetch("/api/battle/invitation-response", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    fromUserId: invite.fromUserId,
                    toUserId: invite.toUserId,
                    status,
                    invitationId: invite.invitationId,
                    battleId: invite.battleId,
                }),
            });

            if (!res.ok) {
                const body = (await res.json()) as { error?: string };
                toast.error(body.error ?? t("actionFailed"));
            } else {
                accepted = status === "accepted";
            }
        } catch {
            toast.error(t("connectionError"));
        } finally {
            setIsResponding(false);
            onRemove(invite.battleId);
        }

        if (accepted) {
            router.push(
                `/bulmacalar/battle/${invite.battleId}` +
                    `?autoJoin=true` +
                    `&host=${encodeURIComponent(invite.fromDisplayUsername ?? "")}` +
                    `&duration=${invite.duration ?? 10}` +
                    `&opponentUserId=${invite.fromUserId}` +
                    `&invitationId=${invite.invitationId}`,
            );
        }
    };

    return (
        <InviteCard
            from={{
                nickname: invite.fromDisplayUsername,
                userId: invite.fromUserId,
            }}
            title={t("battleInviteReceived")}
            subtitle={
                invite.duration ? `${invite.duration} sn / bulmaca` : undefined
            }
            onAccept={() => void respond("accepted")}
            onDecline={() => void respond("declined")}
            isLoading={isResponding}
            acceptLabel={t("join")}
        />
    );
}
